/**
 * Request analytics — the parts of the screen that are logic rather than
 * layout, kept pure so they are testable without a browser.
 *
 * Two of them guard against a quietly wrong reading: the date window, which
 * the server filters in UTC whatever the viewer's clock says, and the CSV
 * export, where a cell beginning "=" would run as a formula in the
 * spreadsheet it is opened in.
 */

import type { RequestEvent, RequestFilters, RequestUserMetrics } from "./types";

const DAY_MS = 86_400_000;

/** The query string every analytics route takes. Blank filters are left out, not sent empty. */
export function analyticsQuery(filters: RequestFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) params.set(key, text);
  }
  return params.toString();
}

/**
 * Two date inputs (YYYY-MM-DD) as the window the server filters on: from the
 * first millisecond of the first UTC day to the last of the second. Null when
 * either is not a date, or the range runs backwards.
 */
export function utcDayRange(from: string, through: string): { from: number; to: number } | null {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${through}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return { from: start, to: end + DAY_MS - 1 };
}

/** The last `days` UTC days, ending today, as date-input values. */
export function defaultRange(now = Date.now(), days = 7): { from: string; through: string } {
  const through = new Date(now).toISOString().slice(0, 10);
  const from = new Date(now - (days - 1) * DAY_MS).toISOString().slice(0, 10);
  return { from, through };
}

/** A UTC day number (days since 1970-01-01) as YYYY-MM-DD. */
export function utcDayLabel(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

/** "340 ms", "1.24 s", "2 min 5 s" — or "—" when nothing was measured. */
export function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`;
  const minutes = Math.floor(ms / 60_000);
  return `${minutes} min ${Math.round((ms - minutes * 60_000) / 1000)} s`;
}

/** The tiles above the report: each figure summed over the users in it. */
export function totals(rows: RequestUserMetrics[]) {
  const sum = (key: keyof RequestUserMetrics) => rows.reduce((n, row) => n + (Number(row[key]) || 0), 0);
  return {
    users: rows.filter((row) => row.user_id).length,
    attempts: sum("turns"),
    failed: sum("failed_turns"),
    cancelled: sum("cancelled_turns"),
    toolCalls: sum("tool_calls"),
    toolErrors: sum("tool_errors"),
    tokens: sum("input_tokens") + sum("output_tokens"),
  };
}

/**
 * One CSV cell. Numbers go as they are; text is quoted with its quotes
 * doubled, and a leading = + - @ tab or carriage return is defused with an
 * apostrophe, so a spreadsheet reads it as text instead of running it.
 */
export function csvCell(value: unknown): string {
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  const text = String(value ?? "");
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export const USER_COLUMNS: { key: keyof RequestUserMetrics; label: string }[] = [
  { key: "email", label: "User" },
  { key: "user_id", label: "User ID" },
  { key: "conversations", label: "Threads" },
  { key: "turns", label: "Finished attempts" },
  { key: "failed_turns", label: "Failed" },
  { key: "cancelled_turns", label: "Cancelled" },
  { key: "tool_calls", label: "Tool calls" },
  { key: "tool_errors", label: "Tool errors" },
  { key: "avg_turn_ms", label: "Average ms" },
  { key: "p95_turn_ms", label: "P95 ms" },
  { key: "input_tokens", label: "Input tokens" },
  { key: "output_tokens", label: "Output tokens" },
  { key: "cached_tokens", label: "Cached tokens" },
];

/** The user report as a CSV file's text: a header row, then one row per user. */
export function usersCsv(rows: RequestUserMetrics[]): string {
  const header = USER_COLUMNS.map((c) => csvCell(c.label)).join(",");
  const lines = rows.map((row) => USER_COLUMNS.map((c) => csvCell(row[c.key])).join(","));
  return [header, ...lines].join("\n");
}

/* ---------------- a trail as a tree ---------------- */

/**
 * One step of a trail. A span — a turn, a model round, a tool, an MCP call,
 * an HTTP request — has an opening and a closing event and may hold other
 * steps; a moment — a message stored, the engine's decision, an approval —
 * is a single event inside whichever span it happened in.
 */
export interface TrailNode {
  key: string;
  kind: string;
  name: string;
  /** When it began: a span's opening, or the moment itself. */
  at: number;
  sequence: number;
  start: RequestEvent | null;
  end: RequestEvent | null;
  /** The event itself, for a moment. */
  moment: RequestEvent | null;
  /** How it ended — or "open" for a span whose end was never recorded. */
  status: string;
  durationMs: number | null;
  children: TrailNode[];
}

const byTime = (a: TrailNode, b: TrailNode) => a.at - b.at || a.sequence - b.sequence;

/**
 * A selection's events, oldest first, as the tree they describe: each span
 * under the span it ran inside (parent_span_id), each moment inside the span
 * it happened in (span_id). A step whose parent is not in the trail starts a
 * root of its own, and a parent loop — impossible in a sound recording, but
 * cheap to survive — is broken rather than followed forever.
 */
export function buildTrail(events: RequestEvent[]): TrailNode[] {
  const ordered = [...events].sort((a, b) => a.at - b.at || a.sequence - b.sequence);
  const spans = new Map<string, TrailNode>();
  const moments: { node: TrailNode; inside: string | null }[] = [];

  for (const e of ordered) {
    if ((e.phase === "start" || e.phase === "end") && e.span_id) {
      let node = spans.get(e.span_id);
      if (!node) {
        node = {
          key: e.span_id, kind: e.kind, name: e.name, at: e.at, sequence: e.sequence,
          start: null, end: null, moment: null, status: "open", durationMs: null, children: [],
        };
        spans.set(e.span_id, node);
      }
      if (e.phase === "start") {
        Object.assign(node, { start: e, at: e.at, sequence: e.sequence, kind: e.kind, name: e.name });
      } else {
        Object.assign(node, { end: e, status: e.status, durationMs: e.duration_ms });
      }
    } else {
      moments.push({
        inside: e.span_id,
        node: {
          key: e.id, kind: e.kind, name: e.name, at: e.at, sequence: e.sequence,
          start: null, end: null, moment: e, status: e.status, durationMs: e.duration_ms, children: [],
        },
      });
    }
  }

  const parentOf = (node: TrailNode): string | null => (node.start ?? node.end)?.parent_span_id ?? null;
  /** Whether `node` sits somewhere above `from` — linking it under `from` would close a loop. */
  const above = (node: TrailNode, from: TrailNode): boolean => {
    let at: string | null = from.key;
    for (let hops = 0; at && hops < 200; hops++) {
      if (at === node.key) return true;
      const next = spans.get(at);
      at = next ? parentOf(next) : null;
    }
    return false;
  };

  const roots: TrailNode[] = [];
  for (const node of spans.values()) {
    const parent = spans.get(parentOf(node) ?? "");
    if (parent && parent !== node && !above(node, parent)) parent.children.push(node);
    else roots.push(node);
  }
  for (const { node, inside } of moments) {
    const span = inside ? spans.get(inside) : undefined;
    (span ? span.children : roots).push(node);
  }

  const sortDeep = (nodes: TrailNode[]) => {
    nodes.sort(byTime);
    for (const node of nodes) sortDeep(node.children);
  };
  sortDeep(roots);
  return roots;
}

/** "+1.24 s" — how long after the trail's first step this one began. */
export function offsetLabel(at: number, base: number): string {
  return `+${formatMs(Math.max(0, at - base))}`;
}

const SCREEN_LABELS: Record<string, string> = {
  home: "Home",
  chat: "Chat",
  chats: "Chats",
  trace: "Work trace",
  desk: "Desk",
  documents: "Documents",
  gallery: "Templates",
  automations: "Automations",
  "erp-operations": "ERP operations",
  approvals: "Approvals",
  analytics: "Analytics",
  reports: "Reports",
  dashboards: "Dashboards",
  inbox: "Inbox",
  intake: "Document intake",
  knowledge: "Knowledge",
  skills: "Skill playbooks",
  operations: "Operations",
  profile: "Profile",
  appearance: "Appearance",
  settings: "Settings",
  console: "Console",
};

/** A route name as the screen it is: "erp-operations" → "ERP operations". */
export function screenLabel(screen: string | null | undefined): string {
  if (!screen) return "Unknown page";
  const known = SCREEN_LABELS[screen];
  if (known) return known;
  const words = screen.replace(/-/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const KIND_LABELS: Record<string, string> = {
  http: "HTTP request",
  message: "Message",
  turn: "Turn",
  turn_state: "Turn state",
  engine: "Engine decision",
  model: "Model round",
  tool: "Tool",
  mcp: "MCP call",
  approval: "Approval",
  activity: "Browser activity",
};

export const STATUS_LABELS: Record<string, string> = {
  ok: "ok",
  error: "error",
  cancelled: "cancelled",
  waiting_approval: "waiting for approval",
  needs_input: "needs input",
  open: "no end recorded",
};

const asRecord = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const asText = (v: unknown): string => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "");
const clip = (s: string, n = 90): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * One line saying what a step was about: the question asked, the doctype a
 * tool read, the answer's first words, why the engine handed over, what went
 * wrong. Empty when the recording says nothing more than the step's name —
 * including whenever content capture is off.
 */
export function stepHint(node: TrailNode): string {
  const input = asRecord(node.start?.payload ?? node.moment?.payload);
  const output = asRecord(node.end?.payload);
  if (node.status === "error") {
    // The message before the code: "validation" says less than the sentence beside it.
    const why = asText(output?.message) || asText(output?.error) || asText(output?.text);
    if (why) return clip(why);
  }
  switch (node.kind) {
    case "message":
    case "turn": {
      const said = asText(input?.text);
      return said ? clip(`“${said}”`) : "";
    }
    case "engine": {
      if (node.name === "answered") return "answered without a model round";
      const reason = asText(input?.declineReason);
      return reason ? `handed to the model — ${reason.replace(/_/g, " ")}` : "handed to the model";
    }
    case "http": {
      const code = output?.statusCode;
      return typeof code === "number" ? `→ ${code}` : "";
    }
    case "tool":
    case "mcp":
      return asText(input?.doctype) || asText(input?.report) || asText(input?.name) || asText(input?.method);
    case "model": {
      const round = input?.round;
      const calls = asRecord(output?.message)?.tool_calls;
      const asked = Array.isArray(calls) && calls.length ? ` · asked for ${calls.length} tool call(s)` : "";
      return typeof round === "number" ? `round ${round + 1}${asked}` : asked.slice(3);
    }
    case "approval":
      return asText(input?.tool) || asText(input?.doctype);
    case "activity":
      return asText(input?.screen);
    default:
      return "";
  }
}

/** "just now", "5 min ago", "3 h ago", "yesterday", then the date. */
export function relativeTime(at: number, now = Date.now()): string {
  const ms = Math.max(0, now - at);
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < DAY_MS) return `${Math.floor(ms / 3_600_000)} h ago`;
  if (ms < 2 * DAY_MS) return "yesterday";
  return new Date(at).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/* ---------------- performance: where the time went, and what to try ---------------- */

export type Severity = "high" | "medium" | "low" | "good";

/** One observation about a request, with the numbers behind it and what to try. */
export interface Finding {
  severity: Severity;
  title: string;
  /** What the recording shows. */
  detail: string;
  /** What to try. */
  fix: string;
}

export interface Diagnosis {
  /** First recorded event to last: what the person waited through. */
  totalMs: number;
  /** Where that time went, by the kind of work — shares of totalMs. */
  breakdown: { key: "model" | "tools" | "waiting" | "calls" | "other"; label: string; ms: number }[];
  counts: {
    modelRounds: number;
    toolCalls: number;
    erpCalls: number;
    httpCalls: number;
    failures: number;
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
  };
  /** Most severe first. */
  findings: Finding[];
}

/**
 * The lines the advice is drawn on, named so each finding can be traced to
 * a number. Deliberately coarse: they point at where to look, and the step
 * tree shows what actually happened.
 */
export const PERF = {
  slowToolMs: 3_000,
  verySlowToolMs: 8_000,
  slowHttpMs: 1_000,
  verySlowHttpMs: 3_000,
  manyRounds: 4,
  tooManyRounds: 6,
  heavyInputTokens: 50_000,
  queueWaitMs: 2_000,
  prepMs: 2_000,
  manyCalls: 15,
  bigOutputChars: 8_000,
} as const;

const SEVERITY_ORDER: Record<Severity, number> = { high: 0, medium: 1, low: 2, good: 3 };

interface Placed {
  node: TrailNode;
  /** Inside a tool span — its ERP calls are the tool's, not counted twice. */
  inTool: boolean;
}

function place(nodes: TrailNode[], inTool = false, out: Placed[] = []): Placed[] {
  for (const node of nodes) {
    out.push({ node, inTool });
    place(node.children, inTool || node.kind === "tool", out);
  }
  return out;
}

/** The length of time covered by at least one of these intervals. */
function covered(intervals: [number, number][]): number {
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  let total = 0;
  let [start, end] = sorted[0] ?? [0, 0];
  for (const [s, e] of sorted.slice(1)) {
    if (s > end) {
      total += end - start;
      [start, end] = [s, e];
    } else end = Math.max(end, e);
  }
  return sorted.length ? total + (end - start) : 0;
}

/** Text in quotation marks — unless it already opens with one of its own. */
const quoted = (text: string) => (!text ? "" : /^["“'‘]/.test(text) ? text : `“${text}”`);
const sumMs = (nodes: TrailNode[]) => nodes.reduce((n, node) => n + (node.durationMs ?? 0), 0);
const human = (code: string) => code.replace(/_/g, " ");
const sizeOf = (value: unknown) => (value == null ? 0 : JSON.stringify(value).length);

/**
 * What one query or page load spent its time on, and what to try — from the
 * recording alone, by fixed rules: no model reads it, so the same trail
 * always gets the same advice.
 */
export function diagnose(events: RequestEvent[], nodes: TrailNode[] = buildTrail(events)): Diagnosis {
  const times = events.map((e) => e.at);
  const totalMs = times.length ? Math.max(...times) - Math.min(...times) : 0;
  const pct = (ms: number) => (totalMs ? `${Math.round((ms / totalMs) * 100)}%` : "—");

  const placed = place(nodes);
  const spans = placed.filter((p) => !p.node.moment);
  const ofKind = (kind: string) => spans.filter((p) => p.node.kind === kind).map((p) => p.node);
  const models = ofKind("model");
  const tools = ofKind("tool");
  const turns = ofKind("turn");
  const https = ofKind("http");
  // ERP calls a tool made are the tool's time; ones made directly (the engine's
  // own reads) are counted on their own.
  const erpOnTheirOwn = spans.filter((p) => p.node.kind === "mcp" && !p.inTool).map((p) => p.node);
  const erpCalls = ofKind("mcp");
  const work = [...tools, ...erpOnTheirOwn];

  const modelEnds = models.map((m) => m.end).filter((e): e is RequestEvent => Boolean(e));
  const inputTokens = modelEnds.reduce((n, e) => n + e.input_tokens, 0);
  const outputTokens = modelEnds.reduce((n, e) => n + e.output_tokens, 0);
  const cachedTokens = modelEnds.reduce((n, e) => n + e.cached_tokens, 0);
  const failures = spans.filter((p) => p.node.status === "error").length;

  const findings: Finding[] = [];
  let breakdown: Diagnosis["breakdown"];

  if (turns.length) {
    /* ---------------- a query ---------------- */
    const modelMs = sumMs(models);
    const toolMs = sumMs(work);
    // Queued: from "turn accepted" to a worker starting the turn.
    const accepted = placed.find((p) => p.node.kind === "turn" && p.node.name === "accepted")?.node;
    const firstRun = turns.find((t) => t.start)?.start;
    const waitingMs = accepted && firstRun ? Math.max(0, firstRun.at - accepted.at) : 0;
    breakdown = [
      { key: "model", label: "Model rounds", ms: modelMs },
      { key: "tools", label: "Tools and ERP calls", ms: toolMs },
      { key: "waiting", label: "Waiting to start", ms: waitingMs },
      { key: "other", label: "Everything else", ms: Math.max(0, totalMs - modelMs - toolMs - waitingMs) },
    ];

    const failedTurn = turns.find((t) => t.status === "error");
    if (failedTurn) {
      findings.push({
        severity: "high",
        title: "The query failed",
        detail: stepHint(failedTurn) || "The turn ended in an error.",
        fix: "Open the failed step in the tree — its output says what went wrong. Fix that first; the timings after a failure are not the query's real cost.",
      });
    }

    const open = spans.filter((p) => p.node.status === "open").map((p) => p.node);
    if (open.length) {
      findings.push({
        severity: "high",
        title: `${open.length} step(s) never finished`,
        detail: `No end was recorded for ${open.map((n) => n.name).slice(0, 4).join(", ")}.`,
        fix: "The server may have restarted or crashed mid-request, or the step is still running. Check the server log around that time.",
      });
    }

    const engine = placed.filter((p) => p.node.kind === "engine").map((p) => p.node);
    if (engine.some((e) => e.name === "answered")) {
      findings.push({
        severity: "good",
        title: "Answered by the engine",
        detail: "The deterministic engine answered without a model round — the fastest path there is.",
        fix: "Nothing to change.",
      });
    } else if (models.length && (models.length >= 2 || modelMs >= 3_000)) {
      const reason = asText(asRecord(engine.find((e) => e.name === "fallback")?.moment?.payload)?.declineReason);
      findings.push({
        severity: "medium",
        title: "Went to the model instead of the engine",
        detail: `${engine.length ? `The engine handed this question to the model${reason ? ` (${human(reason)})` : ""}` : "No engine decision was recorded"}; the model then took ${models.length} round(s) and ${formatMs(modelMs)}.`,
        fix: "If this is a question people ask often, give it a deterministic answer — a report answer (Operations → Reports → Make an answer) or a metric definition — so it is answered in about a second with no model round. If the engine should have understood the wording, add it to that definition's phrases.",
      });
    }

    if (models.length >= PERF.manyRounds) {
      findings.push({
        severity: models.length >= PERF.tooManyRounds ? "high" : "medium",
        title: `${models.length} model rounds`,
        detail: `They took ${formatMs(modelMs)} (${pct(modelMs)} of the time). Every round re-sends the conversation so far — about ${Math.round(inputTokens / models.length).toLocaleString("en-US")} input tokens a round here.`,
        fix: "Aim for one or two rounds: a tool or skill that returns everything the answer needs in one call, tool descriptions clear enough that the first choice is right, and no rounds spent recovering from failed calls (see below).",
      });
    }

    for (const tool of [...work].filter((t) => (t.durationMs ?? 0) >= PERF.slowToolMs).sort((a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0)).slice(0, 3)) {
      const erpInside = sumMs(tool.children.filter((c) => c.kind === "mcp" && !c.moment));
      const insideGenie = tool.kind === "tool" && erpInside > 0 && erpInside < (tool.durationMs ?? 0) * 0.5;
      const what = stepHint(tool);
      findings.push({
        severity: (tool.durationMs ?? 0) >= PERF.verySlowToolMs ? "high" : "medium",
        title: `${tool.name} took ${formatMs(tool.durationMs)}`,
        detail: `${pct(tool.durationMs ?? 0)} of the time${what ? `, reading ${what}` : ""}.${insideGenie ? ` Only ${formatMs(erpInside)} of it was the ERP call.` : ""}`,
        fix: insideGenie
          ? "Most of the time was Genie's own work inside the tool, not the ERP: look at what the tool does with the result (formatting, joins, follow-up reads)."
          : "Open the step to see exactly what it asked the ERP for. Narrow the filters or date range, fetch only the fields needed, page through large sets, or answer from an ERPNext report built for this question. A question asked often could be cached.",
      });
    }

    // A failed call inside a tool that failed too is one failure, told once.
    const failed = work.filter((t) => t.status === "error");
    for (const call of failed.slice(0, 3)) {
      const recovery = models.find((m) => m.at >= (call.end?.at ?? call.at));
      findings.push({
        severity: "medium",
        title: `${call.name} failed`,
        detail: `${quoted(stepHint(call)) || "It returned an error."}${recovery ? ` The model then spent another round (${formatMs(recovery.durationMs)}) working around it.` : ""}`,
        fix: "The error says what was wrong with the call. Make the right call obvious the first time — correct the tool's description or input schema, or the skill's instructions, for this case.",
      });
    }

    const seen = new Map<string, TrailNode[]>();
    for (const call of work) {
      const input = call.start?.payload;
      if (input == null) continue;
      const key = `${call.name} ${JSON.stringify(input)}`;
      seen.set(key, [...(seen.get(key) ?? []), call]);
    }
    for (const repeats of [...seen.values()].filter((r) => r.length > 1).slice(0, 2)) {
      findings.push({
        severity: "low",
        title: `${repeats[0].name} ran ${repeats.length} times with the same input`,
        detail: `${formatMs(sumMs(repeats))} in all, for data already fetched once.`,
        fix: "Keep the first result where the model can use it — return it in a shape it can cite — so it does not ask for the same data again.",
      });
    }

    if (inputTokens >= PERF.heavyInputTokens) {
      const biggest = [...work].sort((a, b) => sizeOf(b.end?.payload) - sizeOf(a.end?.payload))[0];
      const big = biggest && sizeOf(biggest.end?.payload) >= PERF.bigOutputChars ? biggest : null;
      findings.push({
        severity: "medium",
        title: `${inputTokens.toLocaleString("en-US")} input tokens`,
        detail: `Over ${models.length} round(s); ${inputTokens ? Math.round((cachedTokens / inputTokens) * 100) : 0}% came from the prompt cache.${big ? ` The largest tool result was ${big.name}, at least ${Math.round(sizeOf(big.end?.payload) / 1000)} KB.` : ""}`,
        fix: "Most input is the conversation and tool results re-sent every round. Have tools return summaries or aggregates rather than every row, keep the system prompt and tool list identical between rounds so the cache holds, and start a new thread for an unrelated question.",
      });
    }

    if (waitingMs >= PERF.queueWaitMs) {
      findings.push({
        severity: "medium",
        title: `Waited ${formatMs(waitingMs)} before starting`,
        detail: "The question was accepted, then queued until a worker picked it up.",
        fix: "Check the job queue on the Operations screen: workers busy with other turns, or too few of them, show up here.",
      });
    }

    const firstRound = models[0];
    const turnStart = firstRun;
    if (firstRound && turnStart && firstRound.at - turnStart.at >= PERF.prepMs) {
      findings.push({
        severity: "low",
        title: `${formatMs(firstRound.at - turnStart.at)} before the first model round`,
        detail: "Genie spent this before asking the model anything: the engine's own attempt (which can read the ERP), and loading context, skills and memory.",
        fix: "Look at the steps before the first model round in the tree — an engine attempt that read the ERP and then declined is the usual cost.",
      });
    }
  } else {
    /* ---------------- a page load ---------------- */
    const intervals: [number, number][] = https
      .filter((h) => h.end)
      .map((h) => [h.start?.at ?? h.end!.at - (h.durationMs ?? 0), h.end!.at]);
    const callsMs = covered(intervals);
    breakdown = [
      { key: "calls", label: "Calls in flight", ms: callsMs },
      { key: "other", label: "Between calls", ms: Math.max(0, totalMs - callsMs) },
    ];

    for (const call of [...https].filter((h) => (h.durationMs ?? 0) >= PERF.slowHttpMs).sort((a, b) => (b.durationMs ?? 0) - (a.durationMs ?? 0)).slice(0, 3)) {
      const erpInside = sumMs(place(call.children).filter((p) => p.node.kind === "mcp" && !p.node.moment).map((p) => p.node));
      // A call that ran a tool is named by the tool: one endpoint runs them all.
      const tool = call.children.find((c) => c.kind === "tool" && !c.moment);
      findings.push({
        severity: (call.durationMs ?? 0) >= PERF.verySlowHttpMs ? "high" : "medium",
        title: `${tool ? `${tool.name} (via ${call.name})` : call.name} took ${formatMs(call.durationMs)}`,
        detail: `${pct(call.durationMs ?? 0)} of the page load.${erpInside ? ` ${formatMs(erpInside)} of it was ERP calls.` : ""}`,
        fix: "Open the call: the steps inside it show where its time went. Cache what does not change between requests, or load it after the page is visible.",
      });
    }

    // Only calls that certainly asked for the same thing count as repeats. A
    // call that ran a tool is the same call only with the same tool and input
    // — /api/tools/execute carries every tool the page uses. A plain call is
    // the same call only when its route has no id in it: two GETs of /:id may
    // well be two different records.
    const repeatKey = (call: TrailNode): { key: string; label: string } | null => {
      const tool = call.children.find((c) => c.kind === "tool" && !c.moment);
      if (tool) {
        return tool.start?.payload == null ? null : { key: `${tool.name} ${JSON.stringify(tool.start.payload)}`, label: `${tool.name} (via ${call.name})` };
      }
      return call.name.includes(":") ? null : { key: call.name, label: call.name };
    };
    const repeated = new Map<string, { label: string; calls: TrailNode[] }>();
    for (const call of https) {
      const id = repeatKey(call);
      if (!id) continue;
      const entry = repeated.get(id.key) ?? { label: id.label, calls: [] };
      entry.calls.push(call);
      repeated.set(id.key, entry);
    }
    for (const { label, calls } of [...repeated.values()].filter((r) => r.calls.length > 1).sort((a, b) => b.calls.length - a.calls.length).slice(0, 3)) {
      findings.push({
        severity: "low",
        title: `${label} asked the same thing ${calls.length} times`,
        detail: `${formatMs(sumMs(calls))} in all on one page load, for the same answer each time.`,
        fix: "Fetch it once for the page and share the result between the parts of the page that need it.",
      });
    }

    const failedCalls = https.filter((h) => h.status === "error");
    if (failedCalls.length) {
      findings.push({
        severity: "medium",
        title: `${failedCalls.length} call(s) failed`,
        detail: failedCalls.slice(0, 4).map((h) => `${h.name} ${stepHint(h)}`.trim()).join("; "),
        fix: "Open each failed call for its status and output. A failure the page recovers from still costs the person a wait.",
      });
    }

    if (https.length >= PERF.manyCalls) {
      findings.push({
        severity: "low",
        title: `${https.length} calls for one page`,
        detail: `${formatMs(sumMs(https))} of calls in all.`,
        fix: "Combine calls the page always makes together into one endpoint, or defer what is not on screen yet.",
      });
    }

    const serialMs = sumMs(https);
    if (https.length >= 3 && serialMs >= 1_500 && callsMs >= serialMs * 0.9) {
      findings.push({
        severity: "medium",
        title: "Calls ran one after another",
        detail: `${formatMs(serialMs)} of calls took ${formatMs(callsMs)} — they barely overlapped.`,
        fix: "Start independent calls together (Promise.all), so the page waits for the slowest call rather than the sum of all of them.",
      });
    }
  }

  if (!findings.length) {
    findings.push({
      severity: "good",
      title: "Nothing stands out",
      detail: `${formatMs(totalMs)} in all, with no failures, slow calls or repeated work.`,
      fix: "Nothing to change.",
    });
  }
  findings.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

  return {
    totalMs,
    breakdown,
    counts: {
      modelRounds: models.length,
      toolCalls: tools.length,
      erpCalls: erpCalls.length,
      httpCalls: https.length,
      failures,
      inputTokens,
      outputTokens,
      cachedTokens,
    },
    findings,
  };
}

/* ---------------- the file to share ---------------- */

/** What the file asks of whoever — or whichever model — reads it. */
export const DIAGNOSIS_PROMPT =
  "This file is one request handled by Go1 Genie, an ERPNext assistant: a deterministic query engine answers what it can, " +
  "and everything else goes to a model that calls ERPNext tools over MCP. `steps` is the request's full trace as a tree — each step's kind " +
  "(HTTP request, turn, engine decision, model round, tool, MCP/ERP call, message), when it started after the first step, how long it " +
  "took, its token counts, and its recorded input and output (credentials removed, long content cut). `findings` are automatic " +
  "observations from fixed rules. Explain where the time went and what caused any failures or extra model rounds, then propose " +
  "specific fixes — to question routing, tool descriptions or skills, ERP queries, or code — ordered by impact.";

interface BundleStep {
  kind: string;
  name: string;
  status: string;
  startedAfterMs: number;
  durationMs: number | null;
  tokens?: { input: number; output: number; cached: number };
  input?: unknown;
  output?: unknown;
  recorded?: unknown;
  steps?: BundleStep[];
}

function bundleSteps(nodes: TrailNode[], base: number): BundleStep[] {
  return nodes.map((node) => {
    const end = node.end ?? node.moment;
    const step: BundleStep = {
      kind: node.kind,
      name: node.name,
      status: node.status,
      startedAfterMs: Math.max(0, node.at - base),
      durationMs: node.durationMs,
    };
    if (end && (end.input_tokens || end.output_tokens || end.cached_tokens)) {
      step.tokens = { input: end.input_tokens, output: end.output_tokens, cached: end.cached_tokens };
    }
    if (node.moment?.payload != null) step.recorded = node.moment.payload;
    if (node.start?.payload != null) step.input = node.start.payload;
    if (node.end?.payload != null) step.output = node.end.payload;
    if (node.children.length) step.steps = bundleSteps(node.children, base);
    return step;
  });
}

/**
 * One query or page load as a file someone can hand to Claude — or read
 * themselves: what it was, where its time went, what the rules found, and
 * every step with what went in and came out.
 */
export function diagnosisBundle(args: {
  title: string;
  selection: Record<string, unknown>;
  events: RequestEvent[];
  truncated: boolean;
  exportedAt?: number;
}) {
  const nodes = buildTrail(args.events);
  const found = diagnose(args.events, nodes);
  const base = args.events.length ? Math.min(...args.events.map((e) => e.at)) : 0;
  return {
    format: "genie-request-diagnosis/1",
    exportedAt: new Date(args.exportedAt ?? Date.now()).toISOString(),
    title: args.title,
    prompt: DIAGNOSIS_PROMPT,
    selection: args.selection,
    summary: { totalMs: found.totalMs, breakdown: found.breakdown, counts: found.counts },
    findings: found.findings,
    /** True when the trail was cut at the server's limit: the steps are the first ones only. */
    truncated: args.truncated,
    steps: bundleSteps(nodes, base),
  };
}
